import { auth } from './firebase-config';
import TransactionAmount, { DeliveryMethod, OrderItem, ProductInventoryItem, ProductItem } from './inventory-types';

const API_BASE_URL = '/v1/inventory-management';

async function getAuthHeaders() {
    const token = await auth.currentUser?.getIdToken();
    return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
    };
}

// Mock data for testing
const mockProductTypes = {
    status: 200,
    data: {
        items: <ProductItem[]>[
            new ProductItem(
                'TestProduct1',
                'Test Product 1',
                new TransactionAmount(new Map([['points', 100]])),
                'consumable',
                'virtual_product'
            ),
            new ProductItem(
                'TestProduct2',
                'Test Product 2',
                new TransactionAmount(new Map([['points', 200]])),
                'permanent',
                'physical_product'
            ),
            new ProductItem(
                'TestProduct3',
                'Test Product 3',
                new TransactionAmount(new Map([['points', 300]])),
                'permanent',
                'digital_product'
            )
        ]
    }
};

const mockOrders = {
    status: 200,
    data: {
        orders: <OrderItem[]>[
            OrderItem.fromJSON({
                instanceId: '1',
                createdDate: new Date().toISOString(),
                userId: 'user123',
                status: 'ordered',
                name: 'Kalle',
                surname: 'Ankka',
                address: 'Kallavägen 1',
                email: 'kalle@ankka.com',
                transactionId: 'txn123',
                productInventoryItem: {
                    instanceId: 'inv123',
                    amount: 1,
                    productTypeId: 'TestProduct1',
                    deliveryMethod: 'virtual_product',
                    instanceData: {'test': 'test', 'test2': 'test2'},
                    createdDate: new Date().toISOString()
                }
            }).toJSON(),
            OrderItem.fromJSON({
                instanceId: '2', 
                createdDate: new Date().toISOString(),
                userId: 'user456',
                status: 'sent',
                name: 'Micke',
                surname: 'Mus',
                address: 'Kallavägen 1',
                email: 'mus@pigg.com',
                transactionId: 'txn456',
                productInventoryItem: {
                    instanceId: 'inv456',
                    amount: 1,
                    productTypeId: 'TestProduct3',
                    deliveryMethod: 'digital_product', 
                    instanceData: {'test': 'test', 'test2': 'test2'},
                    createdDate: new Date().toISOString()
                }
            }).toJSON(),
            OrderItem.fromJSON({
                instanceId: '3',
                createdDate: new Date().toISOString(),
                userId: 'user123',
                status: 'cancelled',
                name: 'Janne',
                surname: 'Långben',
                address: 'Långbenvägen 1',
                email: 'janne@ankka.com',
                transactionId: 'txn123',
                productInventoryItem: {
                    instanceId: 'inv123',
                    amount: 1,
                    productTypeId: 'TestProduct2',
                    deliveryMethod: 'physical_product',
                    instanceData: {},
                    createdDate: new Date().toISOString()
                }
            }).toJSON()
        ]
    }
};

const mockInventoryItems = {
    status: 200,
    data: {
        items: <ProductInventoryItem[]>[
            ProductInventoryItem.fromJSON({ 
                productTypeId: 'TestProduct1', 
                instanceId: '1', 
                amount: 10, 
                deliveryMethod: DeliveryMethod.DIGITAL_PRODUCT,
                createdDate: new Date().toISOString(), 
                instanceData: {'test': 'test', 'test2': 'test2'} 
            }).toJSON()
        ]
    }
};

// Mock API functions
const useMocks = false; // Toggle this to switch between mock and real API calls


export async function getProductTypes(): Promise<ProductItem[]> {
    if (useMocks) return mockProductTypes.data.items;
    const response = await fetch(`${API_BASE_URL}/product-types?ts=${new Date().getTime()}`, {
        headers: await getAuthHeaders(),
    });
    if (response.status !== 200) return [];
    return (await response.json()).data.products.map((item: any) => ProductItem.fromJSON(item));
}

export async function updateProductType(item: ProductItem): Promise<Response> {
    if (useMocks) return new Response(JSON.stringify({ success: true }));
    const response = await fetch(`${API_BASE_URL}/product-types`, {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify({ item: item.toJSON() }),
    });
    return response;
}

export async function getOrders(): Promise<OrderItem[]> {
    if (useMocks) return mockOrders.data.orders;
    const response = await fetch(`${API_BASE_URL}/orders?ts=${new Date().getTime()}`, {
        headers: await getAuthHeaders(),
    });
    if (response.status !== 200) return [];
    return (await response.json()).data.orders.map((order: any) => OrderItem.fromJSON(order));
}

export async function updateOrderStatus(orderId: string, status: string): Promise<Response> {
    if (useMocks) return new Response(JSON.stringify({ success: true }));
    const response = await fetch(`${API_BASE_URL}/orders/${orderId}`, {
        method: 'PUT',
        headers: await getAuthHeaders(),
        body: JSON.stringify({ status }),
    });
    return response;
} 

export async function getInventoryItems(productId: string): Promise<ProductInventoryItem[]> {
    if (useMocks) return mockInventoryItems.data.items;
    const response = await fetch(`${API_BASE_URL}/product-type/${productId}/inventory-items?ts=${new Date().getTime()}`, {
        headers: await getAuthHeaders(),
    });
    if (response.status !== 200) return [];
    return (await response.json()).data.items.map((item: any) => ProductInventoryItem.fromJSON(item));
}

export async function addInventoryItem(item: ProductInventoryItem): Promise<Response> {
    if (useMocks) return new Response(JSON.stringify({ success: true }));
    const response = await fetch(`${API_BASE_URL}/product-type/${item.productTypeId}/inventory-items`, {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify({ item: item.toJSON() }),
    });
    return response;
}
